import {ChangeDetectionStrategy, Component, computed, inject, signal} from '@angular/core'
import {DecimalPipe} from '@angular/common'
import {BetType} from '@/api/player-api/models/bet-type'
import {BetslipCreateService} from '@/features/betslip/betslip-create/betslip-create.service'
import {RiskLimitDto} from '@/api/player-api/models/risk-limit-dto'
import {BetslipOperations} from '@/features/betslip/betslip-create/helpers/betslip-operations'
import {BetslipProblemType} from '@/features/betslip/betslip-create/lib/models/betslip-problem'
import {BetslipSelection, OddsChangeFlag} from '@/features/betslip/betslip-create/lib/models/betslip-selection'
import {BetslipSystem} from '@/features/betslip/betslip-create/lib/models/betslip-system'
import {BetslipWorkbenchStore, RequestedBetType} from './betslip-workbench.store'
import {WorkbenchRiskService} from './workbench-risk.service'
import {RiskInfoStatus} from './mock-betslip-risk-backend'
import {LiveOddsTicker} from './live-odds-ticker'

const TYPE_LABELS: Record<BetType, string> = {
	[BetType.single]: 'Single',
	[BetType.multiSingle]: 'Multi-single',
	[BetType.combo]: 'Combo',
	[BetType.betBuilderCombo]: 'Bet Builder Combo',
	[BetType.system]: 'System',
	[BetType.betBuilderSystem]: 'Bet Builder System'
}

interface CeilingRow {
	label: string
	autoAccept: number
	vip: number
}

/** What the risk reply says, in the shape the Betslip Type asks for. */
interface ReceivedCeilings {
	note?: string
	rows: CeilingRow[]
}

@Component({
	selector: 'dt-workbench-betslip-panel',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [DecimalPipe],
	template: `
		<header class="head">
			<h2>Betslip</h2>
			<span class="type">{{ typeLabel() }}</span>
			@if (data().selections.length) {
				<button type="button" class="link" (click)="store.clear()">clear</button>
			}
		</header>

		<nav class="tabs">
			@for (tab of tabs; track tab.requested) {
				<button type="button" [class.active]="store.requested() === tab.requested" (click)="store.request(tab.requested)">
					{{ tab.label }}
				</button>
			}
		</nav>

		@if (!data().selections.length) {
			<p class="hint">Pick selections from the offer to build a Betslip.</p>
		} @else {
			<ul class="selections">
				@for (selection of data().selections; track selection.oddsId) {
					<li>
						<div>
							<span class="teams">{{ selection.matchName }}</span>
							<span class="market">{{ selection.marketLabel }} — {{ selection.outcomeLabel }}</span>
						</div>
						@if (data().betType === BetType.multiSingle) {
							<input class="own-stake" type="number" min="0" step="1"
								   title="This Selection's own stake — the Betslip's stake is the sum of them"
								   [value]="selection.stake ?? 0" (input)="onSelectionStake(selection, $event)">
						}
						@let move = ticker.moves().get(selection.oddsId);
						<!-- The arrow keeps production's oddsChangeFlag, which holds until the next update;
							 the colour is the fade, which only lasts while the move is fresh. -->
						<span class="odds" [class.up]="selection.oddsChangeFlag === OddsChangeFlag.up"
							  [class.down]="selection.oddsChangeFlag === OddsChangeFlag.down"
							  [class.fade-a]="move && move.seq % 2 === 1"
							  [class.fade-b]="move && move.seq % 2 === 0"
							  [title]="oddsTitle(selection.oddsChangeFlag)">
							<span class="arrow">{{ arrow(selection.oddsChangeFlag) }}</span>
							{{ selection.odds | number: '1.2-2' }}
						</span>
						<button type="button" class="link" (click)="store.remove(selection)">&times;</button>
					</li>
				}
			</ul>

			@if (isSystem() && data().systems.length) {
				<h3>System Options</h3>
				<p class="hint">
					@if (data().betType === BetType.betBuilderSystem) {
						K counts <strong>matches</strong>, not Selections.
					} @else {
						N/N is the same bet as a Combo; 1/N is the same as a Multi-single.
					}
					An Option's stake is editable either way round — per Combination, or as the Option's
					total — and the Betslip's stake is the sum over the ticked Options.
				</p>

				<label class="check all">
					<input type="checkbox" [checked]="store.allSystemsChecked()" (change)="onSelectAll($event)">
					<span>Select all</span>
				</label>

				<table class="systems">
					<thead>
						<tr>
							<th>Option</th>
							<th>Combs</th>
							<th>Stake/comb</th>
							<th>Comb. stake</th>
							<th>Max payout</th>
						</tr>
					</thead>
					<tbody>
						@for (row of data().systems; track row.count) {
							<tr [class.checked]="row.checked">
								<td>
									<label class="check">
										<input type="checkbox" [checked]="row.checked" (change)="store.toggleSystem(row)">
										<span>{{ row.count }}/{{ systemTotal() }}</span>
									</label>
								</td>
								<td>{{ row.numberOfCombs | number }}</td>
								<td>
									<input class="row-stake" type="number" min="0" step="1" [value]="row.stakePerComb"
										   (input)="onSystemStakePerComb(row, $event)">
								</td>
								<td>
									<input class="row-stake" type="number" min="0" step="1" [value]="row.combinationStake"
										   (input)="onCombinationStake(row, $event)">
								</td>
								<td>{{ row.maxPayout | number: '1.2-2' }}</td>
							</tr>
						}
					</tbody>
				</table>
			}

			<div class="inputs">
				<label>
					Stake
					<input type="number" min="0" step="1" [value]="typedStake()" (input)="onStake($event)">
				</label>
				@if (isSystem()) {
					<label>
						Stake per Combination
						<input type="number" min="0" step="1" [value]="globalStakePerComb()"
							   (input)="onStakePerComb($event)">
						<em class="note">Sets every Option at once; Stake spreads a total over them instead.</em>
					</label>
				}
				@if (data().uiData.showPayoutAsInput) {
					<label>
						Possible Payout
						<input type="number" min="0" step="1" [value]="payoutInput()" (input)="onPayout($event)">
					</label>
				} @else {
					<label class="readonly">
						Possible Payout
						<input type="number" [value]="payoutInput()" disabled>
						<em>Production shows no payout input for this Betslip Type.</em>
					</label>
				}
			</div>

			<dl class="figures">
				@if (data().stake !== typedStake()) {
					<div class="capped">
						<dt>Stake after limits</dt>
						<dd>{{ data().stake | number: '1.2-2' }}</dd>
					</div>
				}
				@if (chargedStake() !== data().stake) {
					<div class="capped">
						<dt title="Every Combination carries the same floored stake">Actually charged</dt>
						<dd>{{ chargedStake() | number: '1.2-4' }}</dd>
					</div>
				}
				<div>
					<dt>{{ isSystem() ? 'System odds' : 'Total odds' }}</dt>
					<dd>{{ data().totalOdds | number: '1.2-2' }}</dd>
				</div>
				<div>
					<dt>Possible Payout</dt>
					<dd>{{ data().payout | number: '1.2-2' }}</dd>
				</div>
				@if (showCombinations()) {
					<div>
						<dt>{{ data().betType === BetType.multiSingle ? 'Singles' : 'Combinations' }}</dt>
						<dd>{{ data().totalCombs | number }}</dd>
					</div>
				}
				@if (data().minOdds !== data().maxOdds) {
					<div>
						<dt>Min / max odds</dt>
						<dd>{{ data().minOdds | number: '1.2-2' }} / {{ data().maxOdds | number: '1.2-2' }}</dd>
					</div>
					<div>
						<dt>Min / max payout</dt>
						<dd>{{ data().minPayout | number: '1.2-2' }} / {{ data().maxPayout | number: '1.2-2' }}</dd>
					</div>
				}
			</dl>

			<h3>Liability ceilings received</h3>
			@if (ceilings().note; as note) {
				<p class="hint">{{ note }}</p>
			}
			@if (ceilings().rows.length) {
				<dl class="figures received">
					@for (row of ceilings().rows; track row.label) {
						<div>
							<dt>{{ row.label }}</dt>
							<dd>{{ money(row.autoAccept) }} <span class="muted">/ VIP</span> {{ money(row.vip) }}</dd>
						</div>
					}
				</dl>
				@if (ceilingApplies()) {
					<p class="not-applied applied">
						<strong>Applied:</strong> the stake is capped at the VIP ceiling, alongside the Stake Limit
						(smallest wins). A stake above the Auto-accept ceiling is placeable as entered and the
						betslip says it will be sent for authorization.
					</p>
				} @else {
					<p class="not-applied">
						Received, <strong>not applied for this Betslip Type</strong> — production reads the
						betslip-level ceiling only, which is the shape a Single or a Combo asks for. Multi-single needs
						one per Selection and System one per Option.
					</p>
				}
			}

			@if (authorizationLimit(); as limit) {
				<p class="notice">Stake above {{ limit | number: '1.2-2' }} will be sent for authorization</p>
			}

			@if (breach(); as breach) {
				<p class="notice">
					@if (breach.overVip) {
						Stake {{ breach.stake | number: '1.2-2' }} is <strong>above the VIP liability ceiling</strong>
						of {{ breach.vip | number: '1.2-2' }}{{ breach.scope }}. Production caps a Single and a Combo
						at that ceiling but has no per-Selection or per-Option shape yet, so this Betslip Type is
						left uncapped and the back end would reject it at placement.
					} @else {
						Stake {{ breach.stake | number: '1.2-2' }} is above the <strong>Auto-accept ceiling</strong>
						of {{ breach.autoAccept | number: '1.2-2' }}{{ breach.scope }}
						@if (breach.vip > 0) { but within the VIP ceiling of {{ breach.vip | number: '1.2-2' }}: }
						@else { with no cap: } placeable as entered, sent for Authorization.
					}
				</p>
			}

			@for (problem of problems(); track problem.type) {
				<p class="problem">{{ problem.message || problem.name }} <code>{{ problem.name }}</code></p>
			}

			<button type="button" class="place" [class.over-liability]="breach()?.overVip" disabled>
				{{ placeLabel() }}
			</button>

			<h3>Record for unit test</h3>
			<p class="hint">
				Copies this Betslip — limits, selections, tab, System Options and the last typed stake or payout — as a
				vector to paste into <code>capping-vectors.json</code>. Expectations are written by hand.
			</p>
			@if (stakeIsSplit()) {
				<p class="hint">
					This Betslip's stake was built out of per-Option or per-Selection amounts, which a vector
					cannot carry: it records the total, {{ data().stake | number: '1.2-2' }}, and a replay spreads
					that evenly.
				</p>
			}
			<div class="record">
				<input type="text" placeholder="name, e.g. combo-live-cap" [value]="vectorName()"
					   (input)="vectorName.set(inputValue($event))">
				<input type="text" placeholder="note (optional)" [value]="vectorNote()"
					   (input)="vectorNote.set(inputValue($event))">
				<button type="button" [disabled]="!validName()" (click)="copyVector()">Copy vector JSON</button>
			</div>
			@if (copyStatus(); as status) {
				<p [class.problem]="!status.ok" [class.recorded]="status.ok">{{ status.message }}</p>
			}

			<p class="hint">
				No rule trace or notices: those are betslip-demo's own engine. Here you see only what production produces.
			</p>
		}
	`
})
export class WorkbenchBetslipPanelComponent {
	readonly store = inject(BetslipWorkbenchStore)
	private readonly risk = inject(WorkbenchRiskService)
	/** The workbench's own, isolated betslip (`provideIsolatedBetslip`), not the player's. */
	private readonly betslip = inject(BetslipCreateService)
	readonly ticker = inject(LiveOddsTicker)
	readonly BetType = BetType
	readonly OddsChangeFlag = OddsChangeFlag

	readonly tabs: {requested: RequestedBetType, label: string}[] = [
		{requested: BetType.single, label: 'Singles'},
		{requested: BetType.combo, label: 'Combo'},
		{requested: BetType.system, label: 'System'}
	]

	readonly data = this.store.data
	readonly typeLabel = computed(() => TYPE_LABELS[this.data().betType])
	readonly isSystem = computed(() => BetslipOperations.isSystemBetslip(this.data().betType))
	readonly showCombinations = computed(() => ![BetType.single, BetType.combo].includes(this.data().betType))

	/** How many Selections (or matches, for a Bet Builder) an Option chooses from. */
	readonly systemTotal = computed(() => {
		const systems = this.data().systems
		return systems.length === 0 ? 0 : systems[systems.length - 1].count
	})

	readonly typedStake = computed(() => {
		const input = this.store.input()
		return input.kind === 'stake' ? input.value : this.data().stake
	})

	readonly payoutInput = computed(() => Math.round(this.data().payout * 100) / 100)

	/** Empty until the operator sets one: production leaves it unset while the Stake drives the Options. */
	readonly globalStakePerComb = computed(() => this.data().globalStakePerComb ?? '')

	/** Whether the stake was built out of the rows rather than typed into the Stake input. */
	readonly stakeIsSplit = computed(() => {
		const kind = this.store.input().kind
		return kind === 'stakePerComb' || kind === 'systemStakes' || kind === 'selectionStakes'
	})

	/** What the Betslip really charges — the payload's stake (BetslipConverterService). */
	readonly chargedStake = computed(() => {
		const data = this.data()
		if (!this.isSystem()) {
			return data.stake
		}
		const charged = data.systems.filter((s) => s.checked).reduce((acc, s) => acc + s.combinationStake, 0)
		return BetslipOperations.roundValue(charged, 4)
	})

	readonly problems = computed(() => this.data().problems.map((problem) => ({
		...problem,
		name: BetslipProblemType[problem.type] ?? String(problem.type)
	})))

	readonly ceilings = computed((): ReceivedCeilings => {
		const last = this.risk.last()
		const data = this.data()

		if (!last) {
			return {note: 'No risk request yet.', rows: []}
		}
		if (last.status !== RiskInfoStatus.ready) {
			return {note: `Status is "${last.status}" — no risk info, so no liability ceiling.`, rows: []}
		}
		if (last.error || !last.response) {
			return {note: last.error ?? 'No reply.', rows: []}
		}

		const response = last.response

		if (data.betType === BetType.multiSingle) {
			return {
				rows: response.selections.map((s) => row(
					data.selections.find((local) => local.oddsId === s.oddsId)?.outcomeLabel ?? `odds ${s.oddsId}`,
					s.riskLimitDto
				))
			}
		}

		if (this.isSystem()) {
			const rows = response.systems.map((s) => row(`${s.count}/${this.systemTotal()}`, s.riskLimitDto))
			return {
				note: 'Per System Option; the Betslip ceiling is their sum over the ticked Options.',
				rows: rows.length > 1
					? [...rows, {label: 'Sum', autoAccept: sum(rows, 'autoAccept'), vip: sum(rows, 'vip')}]
					: rows
			}
		}

		return {rows: [row('Whole Betslip', response.riskLimitDto)]}
	})

	/**
	 * Whether the stake is over a ceiling the backend sent, in the shape the Betslip Type asks for:
	 * the whole Betslip, each Selection's own share for a Multi-single, the sum over the
	 * ticked Options for a System.
	 *
	 * The stake is capped at the VIP tier; between Auto-accept and VIP the Betslip is placeable and
	 * goes for Authorization instead. Production applies neither, so this is a warning only.
	 */
	readonly breach = computed((): LiabilityBreach | undefined => {
		const last = this.risk.last()
		const data = this.data()

		if (last?.status !== RiskInfoStatus.ready || !last.response || data.selections.length === 0) {
			return undefined
		}

		const response = last.response

		if (data.betType === BetType.multiSingle) {
			const over = data.selections
				.map((selection) => ({
					selection,
					limit: response.selections.find((s) => s.oddsId === selection.oddsId)?.riskLimitDto
				}))
				.map(({selection, limit}) => breachOf(selection.stake ?? 0, limit, ` for ${selection.outcomeLabel}`))
				.filter((breach): breach is LiabilityBreach => !!breach)

			// The worst one: a Multi-single is capped Selection by Selection, but one line is enough to warn.
			return over.find((breach) => breach.overVip) ?? over[0]
		}

		if (this.isSystem()) {
			const ticked = data.systems.filter((system) => system.checked)
			const limits = ticked.map((system) => response.systems.find((s) => s.count === system.count)?.riskLimitDto)
			// An Option with no ceiling leaves the sum unbounded, as in the ceilings list above.
			const sum = (key: 'upperLimit' | 'vipUpperLimit') => limits.some((limit) => !((limit?.[key] ?? 0) > 0))
				? 0
				: limits.reduce((acc, limit) => acc + (limit?.[key] ?? 0), 0)

			return breachOf(this.chargedStake(), {upperLimit: sum('upperLimit'), vipUpperLimit: sum('vipUpperLimit')},
				' summed over the ticked Options')
		}

		// Single and Combo: between the tiers production's own notice speaks, so only a cut is left to warn of.
		const breach = breachOf(data.stake, response.riskLimitDto, '')
		return breach?.overVip ? breach : undefined
	})

	/**
	 * The production betslip's own authorization notice (`getAuthorizationLimit`), from the Betting
	 * Limits and the liability reply alike. Read again on every push of the betslip.
	 */
	readonly authorizationLimit = computed(() => {
		this.data()
		return this.betslip.getAuthorizationLimit()
	})

	/** Production only consumes the betslip-level ceiling, which is a Single's and a Combo's shape. */
	readonly ceilingApplies = computed(() =>
		this.data().betType === BetType.single || this.data().betType === BetType.combo)

	readonly placeLabel = computed(() => {
		if (this.data().hasProblems) {
			return 'Cannot place'
		}

		return this.breach()?.overVip ? 'Would place — over the liability ceiling' : 'Would place'
	})

	readonly vectorName = signal('')
	readonly vectorNote = signal('')
	readonly copyStatus = signal<{ok: boolean, message: string} | undefined>(undefined)
	readonly validName = computed(() => /^[a-z0-9]+(-[a-z0-9]+)*$/.test(this.vectorName().trim()))

	/** Always rendered, so the arrow's slot keeps the odds in place when there is no change. */
	arrow(flag: OddsChangeFlag): string {
		return flag === OddsChangeFlag.up ? '▲' : flag === OddsChangeFlag.down ? '▼' : ''
	}

	/** Production's `oddsChangeFlag` holds the last feed update's direction until the next update. */
	oddsTitle(flag: OddsChangeFlag): string {
		return flag === OddsChangeFlag.none ? '' : `Odds went ${flag} on the last live update (oddsChangeFlag)`
	}

	money(value: number): string {
		return value > 0 ? value.toFixed(2) : 'none'
	}

	inputValue(event: Event): string {
		return (event.target as HTMLInputElement).value
	}

	/**
	 * The number in a money input, or nothing while the box is empty. Every keystroke recalculates,
	 * so an empty box is someone part-way through typing, not a 0 — pushing 0 would let the binding
	 * write "0" back under the caret. A half-typed "1." reaches us as empty too, and waits.
	 */
	private typedNumber(event: Event): number | undefined {
		const value = (event.target as HTMLInputElement).value

		return value === '' ? undefined : Number(value)
	}

	onStake(event: Event): void {
		const stake = this.typedNumber(event)
		if (stake !== undefined) {
			this.store.setStake(stake)
		}
	}

	onPayout(event: Event): void {
		const payout = this.typedNumber(event)
		if (payout !== undefined) {
			this.store.setPayout(payout)
		}
	}

	onStakePerComb(event: Event): void {
		const stakePerComb = this.typedNumber(event)
		if (stakePerComb !== undefined) {
			this.store.setStakePerComb(stakePerComb)
		}
	}

	onSystemStakePerComb(system: BetslipSystem, event: Event): void {
		const stakePerComb = this.typedNumber(event)
		if (stakePerComb !== undefined) {
			this.store.setSystemStakePerComb(system, stakePerComb)
		}
	}

	onCombinationStake(system: BetslipSystem, event: Event): void {
		const combinationStake = this.typedNumber(event)
		if (combinationStake !== undefined) {
			this.store.setCombinationStake(system, combinationStake)
		}
	}

	onSelectionStake(selection: BetslipSelection, event: Event): void {
		const stake = this.typedNumber(event)
		if (stake !== undefined) {
			this.store.setSelectionStake(selection, stake)
		}
	}

	onSelectAll(event: Event): void {
		this.store.selectAllSystems((event.target as HTMLInputElement).checked)
	}

	async copyVector(): Promise<void> {
		const vector = this.store.toVector(this.vectorName().trim(), this.vectorNote().trim())

		try {
			await navigator.clipboard.writeText(JSON.stringify(vector, null, '\t'))
			this.copyStatus.set({ok: true, message: `Copied "${vector.name}" — paste it into the array in capping-vectors.json.`})
		} catch (error) {
			this.copyStatus.set({ok: false, message: `Could not copy: ${error instanceof Error ? error.message : String(error)}`})
		}
	}
}

/** A ceiling the stake is over: `overVip` means the stake would be cut, otherwise Authorization. */
interface LiabilityBreach {
	stake: number
	autoAccept: number
	vip: number
	overVip: boolean
	/** Which ceiling it is, for the message — "", " for Over 2.5", " summed over the ticked Options". */
	scope: string
}

function breachOf(stake: number, limit: Pick<RiskLimitDto, 'upperLimit' | 'vipUpperLimit'> | undefined,
	scope: string): LiabilityBreach | undefined {
	const autoAccept = limit?.upperLimit ?? 0
	const vip = limit?.vipUpperLimit ?? 0

	// 0 is "no ceiling", and a tier the operator left unset cannot be breached.
	if (vip > 0 && stake > vip) {
		return {stake, autoAccept, vip, overVip: true, scope}
	}
	if (autoAccept > 0 && stake > autoAccept) {
		return {stake, autoAccept, vip, overVip: false, scope}
	}

	return undefined
}

function row(label: string, limit: RiskLimitDto | undefined): CeilingRow {
	return {label, autoAccept: limit?.upperLimit ?? 0, vip: limit?.vipUpperLimit ?? 0}
}

/** An Option with no ceiling leaves the sum unbounded — summing "no limit" with a number would invent one. */
function sum(rows: CeilingRow[], key: 'autoAccept' | 'vip'): number {
	return rows.some((r) => !(r[key] > 0)) ? 0 : rows.reduce((acc, r) => acc + r[key], 0)
}
